/**
 * Granular cookie-consent state (UK GDPR / PECR).
 *
 * Replaces the old binary Accept/Decline consent with:
 * - Two categories: analytics and marketing (essential is always on).
 * - "Accept all" / "Reject all" with equal prominence (ICO requirement).
 * - A reopenable preferences panel ("Cookie settings" in the footer fires
 *   the `pp:open-cookie-settings` window event).
 * - A durable consent record: choice + version + timestamp stored in the
 *   `pp_consent` cookie AND posted server-side via recordConsentChoice so we
 *   can demonstrate consent (and refusals) later.
 * - Cookie purge on rejection across domain variants — the old handler only
 *   removed a few names on the current path, leaving `.pricingpenguin.co.uk`
 *   cookies behind.
 *
 * Backward compatibility: the legacy `CookieConsent` cookie is still written
 * ("true" iff analytics granted) because older sessions/tests read it, and an
 * existing legacy value is migrated so returning visitors aren't re-prompted.
 */
import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { recordConsentChoice, clearPendingEvents } from "../utils/analyticsClient";
import { reconcileAttributionConsent } from "../utils/analytics";
import { setDocumentConsent } from "../utils/consent";

export const CONSENT_COOKIE = "pp_consent";
export const LEGACY_CONSENT_COOKIE = "CookieConsent";
// Bump when the banner text / categories change materially — the stored
// version is part of the consent record.
export const CONSENT_VERSION = 2;
const CONSENT_EXPIRY_DAYS = 365;

// Domain variants a tag may have set cookies on. `undefined` = current host.
const COOKIE_DOMAINS = [undefined, ".pricingpenguin.co.uk", "www.pricingpenguin.co.uk"];

// Known non-essential cookies by category. Prefixes cover dynamic names like
// _ga_<CONTAINER>. IDE/__gpi-style third-party cookies on google.com domains
// cannot be deleted from our origin — denying ad_storage stops them being used.
const ANALYTICS_COOKIES = ["_ga", "_gid", "_gat", "_clck", "_clsk", "CLID", "ANONCHK", "SM"];
const ANALYTICS_COOKIE_PREFIXES = ["_ga_", "_gat_"];
const MARKETING_COOKIES = ["_gcl_au", "__gads", "__gpi", "_fbp", "receive-cookie-deprecation"];
const MARKETING_COOKIE_PREFIXES = ["_gcl_"];

function removeEverywhere(name) {
  COOKIE_DOMAINS.forEach((domain) => {
    Cookies.remove(name, domain ? { path: "/", domain } : { path: "/" });
  });
}

function listCookieNames() {
  try {
    return document.cookie
      .split(";")
      .map((part) => part.split("=")[0].trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function purgeCookieCategory(exact, prefixes) {
  const names = listCookieNames();
  names.forEach((name) => {
    if (exact.includes(name) || prefixes.some((p) => name.startsWith(p))) {
      removeEverywhere(name);
    }
  });
  // Also remove the exact names outright in case document.cookie is unreadable.
  exact.forEach(removeEverywhere);
}

function readStoredConsent() {
  try {
    const raw = Cookies.get(CONSENT_COOKIE);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.v) {
        return {
          analytics: parsed.analytics === true,
          marketing: parsed.marketing === true,
          decided: true,
        };
      }
      return { analytics: false, marketing: false, decided: false };
    }
  } catch {
    // A corrupt granular record must not fall back to an older broad grant.
    return { analytics: false, marketing: false, decided: false };
  }
  const legacy = Cookies.get(LEGACY_CONSENT_COOKIE);
  if (legacy === "true") return { analytics: true, marketing: true, decided: true };
  if (legacy === "false") return { analytics: false, marketing: false, decided: true };
  return { analytics: false, marketing: false, decided: false };
}

const useCookieConsent = () => {
  const [consent, setConsent] = useState({ analytics: false, marketing: false });
  const [consentDecided, setConsentDecided] = useState(true); // true until read, avoids banner flash
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    setConsent({ analytics: stored.analytics, marketing: stored.marketing });
    setConsentDecided(stored.decided);
  }, []);

  // The footer's "Cookie settings" link (or anything else) can reopen the panel.
  useEffect(() => {
    const open = () => setSettingsOpen(true);
    window.addEventListener("pp:open-cookie-settings", open);
    return () => window.removeEventListener("pp:open-cookie-settings", open);
  }, []);

  const persist = useCallback((choices, method) => {
    const next = {
      analytics: choices.analytics === true,
      marketing: choices.marketing === true,
    };
    setDocumentConsent(next);

    try {
      Cookies.set(
        CONSENT_COOKIE,
        JSON.stringify({ v: CONSENT_VERSION, ts: new Date().toISOString(), ...next }),
        { expires: CONSENT_EXPIRY_DAYS, sameSite: "Lax" }
      );
      // Legacy cookie kept for older readers; analytics is its closest meaning.
      Cookies.set(LEGACY_CONSENT_COOKIE, next.analytics ? "true" : "false", {
        expires: CONSENT_EXPIRY_DAYS,
        sameSite: "Lax",
      });

      if (!next.analytics) {
        purgeCookieCategory(ANALYTICS_COOKIES, ANALYTICS_COOKIE_PREFIXES);
      }
      if (!next.marketing) {
        purgeCookieCategory(MARKETING_COOKIES, MARKETING_COOKIE_PREFIXES);
      }
    } catch {
      /* cookie writes can fail in odd embeds; state still updates below */
    }

    // Server-side consent record — sent for refusals too (accountability).
    if (!next.analytics) clearPendingEvents();
    reconcileAttributionConsent(next);
    recordConsentChoice({ ...next, method, version: CONSENT_VERSION });

    setConsent(next);
    setConsentDecided(true);
    setSettingsOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => persist({ analytics: true, marketing: true }, "accept_all"),
    [persist]
  );
  const rejectAll = useCallback(
    () => persist({ analytics: false, marketing: false }, "reject_all"),
    [persist]
  );
  const saveChoices = useCallback(
    (choices) => persist(choices, "custom"),
    [persist]
  );
  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  return {
    consent,
    consentDecided,
    settingsOpen,
    acceptAll,
    rejectAll,
    saveChoices,
    openSettings,
    closeSettings,
    // Back-compat aliases (old API): consentGiven gated analytics-ish tags.
    consentGiven: consent.analytics,
    handleAccept: acceptAll,
    handleDecline: rejectAll,
  };
};

export default useCookieConsent;
